/*
 * Jira Ticket:
 * Created Date: Fri, 21st Feb 2020, 15:43:03 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import defaultTheme from 'ra-ui-materialui/lib/defaultTheme';
import { Notification } from 'ra-ui-materialui/lib/layout';
import {
  useDataProvider,
  useLogin,
  useNotify,
  useRedirect,
  useTranslate,
} from 'react-admin';
import classnames from 'classnames';
import { Card } from '@material-ui/core';
import RegistrationForm from './form';
import TermsAndConditionsDialog from 'resources/auth/TermsAndConditions';
import { pageStyles as styles } from 'resources/auth/styles';

const sanitizeRestProps = ({
  classes,
  className,
  location,
  title,
  array,
  theme,
  dispatch,
  staticContext,
  ...rest
}) => rest;

const Register = withStyles(styles)(({ classes, className, ...rest }) => {
  const [showDialog, setDialogVisibility] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [termsAndConditions, setTermsAndConditions] = useState(null);
  const raLogin = useLogin();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  useEffect(() => {
    let isSubscribed = true;
    const getTCs = async () => {
      return dataProvider('TERMS_CONDITIONS', 'Authentication', {})
        .then((result) => {
          if (isSubscribed && result.data) setTermsAndConditions(result.data);
        })
        .catch((error) => {
          redirect('/');
          return notify(translate('error.auth.unableToGetTCs'), 'warning');
        });
    };
    getTCs();
    return () => (isSubscribed = false);
  }, [dataProvider, notify, translate, redirect]);

  const handleRequestRegistration = async (auth) => {
    await setFormValues(auth);
    return setDialogVisibility(true);
  };

  const handleAcceptTCs = async (setLoading) => {
    setLoading(true);
    return dataProvider('REGISTER', 'Authentication', formValues)
      .then(() => {
        raLogin({
          username: formValues.email,
          password: formValues.password,
        })
          .then((user) => {
            // redirect user if their window location is still on login
            if (!user.signInUserSession.idToken.payload.id) {
              // for some reason sometimes the id is in cognito:username
              user.signInUserSession.idToken.payload.id =
                user.signInUserSession.idToken.payload.sub ||
                user.signInUserSession.idToken.payload['cognito:username'] ||
                user.username;
            }
            localStorage.removeItem('initialAppViewDone');
            localStorage.setItem(
              'userSub',
              user.signInUserSession.idToken.payload.id
            );
            redirect('/');
          })
          .catch((error) => {
            const {
              extensions: { code },
              message,
            } = error.graphQLErrors[0];
            if (code !== 'INTERNAL_SERVER_ERROR') {
              return notify(message, 'warning');
            }
            return notify('error.generic', 'warning');
          });
      })
      .catch((error) => {
        setLoading(false);
        if (String(error).includes('already exists'))
          return notify(
            translate('error.auth.accountAlreadyExists'),
            'warning'
          );
        if (String(error).includes('Unable to verify certificate details'))
          return notify(translate('error.auth.unableToVerifyCert'), 'warning');
        return notify(translate('error.auth.errorInReg'), 'warning');
      });
  };

  return (
    <div
      className={classnames(classes.main, className)}
      {...sanitizeRestProps(rest)}
    >
      <Card className={classes.card}>
        <RegistrationForm requestRegistration={handleRequestRegistration} />
      </Card>
      <TermsAndConditionsDialog
        dialogOpen={showDialog}
        termsAndConditions={termsAndConditions}
        setDialogState={setDialogVisibility}
        handleAcceptTCs={handleAcceptTCs}
      />
      <Notification />
    </div>
  );
});

Register.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object,
  meta: PropTypes.object,
  theme: PropTypes.object,
};

Register.defaultProps = {
  className: null,
  classes: null,
  meta: null,
  theme: defaultTheme,
};

export default Register;
